import { useEffect, useMemo, useRef, useState } from 'react';
import RoomContentsListInterface, {
  RoomContentsListData,
} from '../../../graphql/Rooms/RoomContentsListInterface';
import { RoomStructureContent } from '../../../definitions/schemas';
import { Mention } from '../../Room/components/BuyerCopilotChats/MentionsNode/MentionPill';
import {
  getMentionableContents,
  matchStructureItems,
} from '../../../lib/helper/RoomStructureHelper';

export interface UseRoomContentsPoolResult {
  /**
   * Complete pool of openable room contents (root + folder-nested), matched
   * against their structure so they carry the room-level display overrides.
   * Merged into the theater-mode pool so a copilot @-mention can open a content
   * nested in a folder that isn't currently in view.
   */
  structureContents: RoomStructureContent[];
  /** Mentionable contents for the copilot @-mention list. */
  mentions: Mention[];
  loading: boolean;
  error: boolean;
}

/**
 * Fetches the full set of room contents by roomId — including contents nested
 * inside folders, which are absent from the room's top-level structure — and
 * derives both the copilot mention list and the theater-mode content pool from
 * a single `{ structure, contents }` payload.
 *
 * Both outputs run through the same `matchStructureItems` join used by the room
 * and folder views, so mention labels and the opened content share one source
 * of truth (same override-aware names, same filtering).
 *
 * The fetch is gated behind `enabled` so it only fires once the copilot is
 * opened — not eagerly when the room loads. The payload is fetched a single
 * time and retained thereafter, so closing and reopening the copilot does not
 * trigger a refetch.
 */
const useRoomContentsPool = (
  roomId: string | undefined,
  enabled: boolean,
  contactId?: string
): UseRoomContentsPoolResult => {
  const [data, setData] = useState<RoomContentsListData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!roomId || !enabled || hasFetched.current) return;

    hasFetched.current = true;
    let active = true;
    setLoading(true);
    setError(false);

    new RoomContentsListInterface(contactId)
      .getRoomContentsList(roomId)
      .then((result) => {
        if (!active) return;
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [roomId, enabled, contactId]);

  const structureContents = useMemo<RoomStructureContent[]>(() => {
    if (!data) return [];
    const [, contents] = matchStructureItems(data.contents, data.structure);
    return contents;
  }, [data]);

  const mentions = useMemo<Mention[]>(() => {
    if (!data) return [];
    return getMentionableContents(data.contents, data.structure);
  }, [data]);

  return { structureContents, mentions, loading, error };
};

export default useRoomContentsPool;
